/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */
import React, { useState, useRef, useEffect } from "react";
import {
  SelectContainer,
  SelectWrapper,
  SelectContent,
  SelectedItems,
  SelectedTag,
  SelectedTagText,
  RemoveButton,
  Placeholder,
  ArrowIcon,
  DropdownContainer,
  SearchInput,
  OptionsList,
  OptionItem,
  CheckIcon,
  NoDataText,
} from "../../../styles/MultiSelect.styled";

interface OptionType {
  [key: string]: string | number;
  label: string;
  value: string | number;
}

interface Iprops {
  data: OptionType[];
  borderColor?: string;
  bgColor?: string;
  color?: string;
  arrowColor: string;
  menuItemColor?: string;
  width?: number;
  height?: number;
  maxWidth?: number;
  fontSize?: number;
  fontWeight?: number;
  placeHolder?: string;
  id: string;
  value: string;
  label?: string;
  default?: any;
  labelColor?: string;
  labelFontSize?: number;
  labelFontWeight?: number;
  isUppercase?: boolean;
  isWithinLabel?: boolean;
  normalLabel?: boolean;
  disabled?: boolean;
  canRemove?: boolean;

  marginLeftMinus?: number;
  iconMarginTop?: string;
  borderRadius?: number;
  callback: Function;
}

const MultiSelectWithSearch = (props: Iprops) => {
  const [selectedValues, setSelectedValues] = useState<string[]>(
    props.default || []
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const options: OptionType[] = props.data;
  const filteredOptions = options.filter((option: OptionType) =>
    String(option[props.value]).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sync with props.default changes - only on mount or when default actually changes
  useEffect(() => {
    if (props.default && Array.isArray(props.default)) {
      setSelectedValues([...props.default]);
    }
  }, [props.default]); // React to changes in props.default

  // Re-sync selected values when data becomes available (for proper label display)
  useEffect(() => {
    if (props.data && props.data.length > 0 && selectedValues.length > 0) {
      // Force a re-render to update labels now that data is available
      setSelectedValues([...selectedValues]);
    }
  }, [props.data]); // Run when data changes

  // Additional sync when both data and default are available but selectedValues is empty
  useEffect(() => {
    if (
      props.data &&
      props.data.length > 0 &&
      props.default &&
      props.default.length > 0 &&
      selectedValues.length === 0
    ) {
      setSelectedValues([...props.default]);
    }
  }, [props.data, props.default]); // Run when either data or default changes

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
          break;
        case "Enter":
          event.preventDefault();
          if (highlightedIndex >= 0) {
            handleOptionClick(filteredOptions[highlightedIndex]);
          }
          break;
        case "Escape":
          setIsOpen(false);
          setSearchTerm("");
          setHighlightedIndex(-1);
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, highlightedIndex, filteredOptions]);

  const handleOptionClick = (
    option: OptionType,
    orSelected: boolean = false
  ) => {
    if (orSelected) return;
    const optionValue = String(option[props.id]);
    const isSelected = selectedValues.includes(optionValue);

    let newSelectedValues: string[];
    if (!isSelected) {
      newSelectedValues = [...selectedValues, optionValue];
      setSelectedValues(newSelectedValues);
      props.callback(newSelectedValues);
      setSearchTerm("");
    }
  };

  const handleRemoveTag = (valueToRemove: string, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const newSelectedValues = selectedValues.filter(
      (val) => val !== valueToRemove
    );
    setSelectedValues(newSelectedValues);
    props.callback(newSelectedValues);
  };

  const handleContainerClick = () => {
    if (!props.disabled) {
      setIsOpen(!isOpen);
    }
  };

  const getSelectedLabels = () => {
    return selectedValues.map((val) => {
      const matchedOption = options.find((opt) => opt[props.id] == val);

      if (matchedOption) {
        const label = matchedOption[props.value];
        return label;
      } else {
        const index = props.data?.findIndex((opt: any) => opt[props.id] == val);
        const originalOption = props.data[index];

        if (index >= 0 && originalOption) {
          const label = originalOption[props.value];
          return label;
        }

        return val; // Fallback to raw value if no match found
      }
    });
  };

  return (
    <SelectContainer ref={containerRef}>
      <SelectWrapper
        $borderColor={props.borderColor}
        $bgColor={props.bgColor}
        $width={props.width}
        $height={props.height}
        $borderRadius={props.borderRadius}
        $marginLeftMinus={props.marginLeftMinus}
        $disabled={props.disabled}
        onClick={handleContainerClick}
      >
        <SelectContent
          $fontSize={props.fontSize}
          $fontWeight={props.fontWeight}
          $color={props.color}
          $height={props.height}
        >
          <SelectedItems>
            {selectedValues.length === 0 ? (
              <Placeholder
                $labelColor={props.labelColor}
                $labelFontSize={props.labelFontSize}
                $labelFontWeight={props.labelFontWeight}
              >
                {props.placeHolder}
              </Placeholder>
            ) : (
              getSelectedLabels().map((label, index) => (
                <SelectedTag key={index}>
                  <SelectedTagText>
                    {label}
                  </SelectedTagText>
                  {props.canRemove && (
                    <RemoveButton
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleRemoveTag(selectedValues[index], e)}
                    >
                      ×
                    </RemoveButton>
                  )}
                </SelectedTag>
              ))
            )}
          </SelectedItems>
          <ArrowIcon $isOpen={isOpen} $color={props.arrowColor} />
        </SelectContent>
      </SelectWrapper>

      <DropdownContainer $isOpen={isOpen}>
        <SearchInput
          ref={searchInputRef}
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          $fontSize={props.fontSize}
          $color={props.color}
        />
        <OptionsList>
          {filteredOptions.length === 0 ? (
            <NoDataText>No data</NoDataText>
          ) : (
            filteredOptions.map((option: OptionType, index) => {
              const isSelected = selectedValues.includes(
                String(option[props.id])
              );
              const isHighlighted = index === highlightedIndex;

              return (
                <OptionItem
                  key={option[props.id]}
                  $isSelected={isSelected}
                  $isHighlighted={isHighlighted}
                  $menuItemColor={props.menuItemColor}
                  onClick={() => handleOptionClick(option, isSelected)}
                >
                  <span>{option[props.value]}</span>
                  <CheckIcon $isVisible={isSelected} />
                </OptionItem>
              );
            })
          )}
        </OptionsList>
      </DropdownContainer>
    </SelectContainer>
  );
};

export default MultiSelectWithSearch;
